import { createRouter, createWebHistory, type RouteMeta } from 'vue-router';
import DashboardLayoutVue from '@/layouts/dashboard.vue';
import DepotLayoutVue from '@/layouts/depot.vue';

import { msalInstance } from '@/util/authConfig';
import { useUserStore } from '@/stores/user.store';

import { routes as DepotRoutes } from './depot/depot.routes';
import { routes as MantleRoutes } from './mantle/mantle.routes';
import { routes as OneTwoFiveRoutes } from './onetwofive/onetwofive.routes';
import { ROUTE_NAMES } from '@/lib/enums';
import { ROLE_125_USER, ROLE_STAFF_USER } from '@/util/roles';

interface IRouteMeta {
	title: string;
	breadcrumb?: string;
	requireAuth?: boolean;
	/** Minimum role required to access this route (hierarchical). Absent = any authenticated user. */
	minRole?: string;
}

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: [
		{
			path: '/',
			redirect: '/dashboard',
		},
		{
			path: '/login',
			name: ROUTE_NAMES.LOGIN,
			component: () => {
				return import('@/views/Login.vue');
			},
			meta: {
				title: 'Login',
			} as RouteMeta & IRouteMeta,
		},
		{
			path: '/dashboard',
			component: DashboardLayoutVue,
			redirect: '/dashboard/overview',
			meta: {
				title: 'Dashboard',
				requireAuth: true,
			},
			children: [
				{
					path: 'overview',
					name: ROUTE_NAMES.OVERVIEW,
					component: () => {
						return import('@/views/dashboard/Overview.vue');
					},
					// Deliberately ungated. This is the launcher every sign-in lands
					// on, and it links to three independently gated sections, so any
					// minRole here is a guess at the union of three unrelated
					// requirements — one that refused Depot.User a page listing a
					// product they may open. The tiles gate themselves against their
					// own destinations instead; see views/dashboard/Overview.vue.
					meta: {
						title: 'Overview',
						requireAuth: true,
					} as RouteMeta & IRouteMeta,
				},
				{
					path: 'forbidden',
					name: ROUTE_NAMES.FORBIDDEN,
					component: () => {
						return import('@/views/Forbidden.vue');
					},
					meta: {
						title: 'Forbidden',
						requireAuth: true,
					} as RouteMeta & IRouteMeta,
				},
			],
		},
		{
			path: '/depot',
			component: DepotLayoutVue,
			// Baseline: every depot route requires at least Staff.User (locks out
			// Depot.User). Write routes/actions override upward to Depot.Admin.
			// Four sections override downward to Depot.User — the dashboard,
			// aggregations, reviews and the single review — each declared on its
			// own route so anything added here is still closed by default.
			meta: {
				title: 'Depot',
				requireAuth: true,
				minRole: ROLE_STAFF_USER,
			} as RouteMeta & IRouteMeta,
			children: DepotRoutes,
		},
		{
			path: '/mantle',
			component: DashboardLayoutVue,
			meta: {
				title: 'Mantle',
				requireAuth: true,
			} as RouteMeta & IRouteMeta,
			children: MantleRoutes,
		},
		{
			path: '/125data',
			component: DashboardLayoutVue,
			// Baseline: every 125 route requires at least 125.User, so anything added
			// under /125data is closed by default. The report builder overrides upward
			// to 125.Admin. Depot roles grant nothing here — the two ladders are
			// separate namespaces, so Depot.Staff.Admin does not open this section.
			meta: {
				title: '125 Data',
				requireAuth: true,
				minRole: ROLE_125_USER,
			} as RouteMeta & IRouteMeta,
			children: OneTwoFiveRoutes,
		},
		{
			path: '/user',
			component: DashboardLayoutVue,
			children: [
				{
					path: 'profile',
					name: ROUTE_NAMES.USER.PROFILE,
					component: () => {
						return import('@/views/User.vue');
					},
					meta: {
						title: 'User Profile',
						requireAuth: true,
					} as RouteMeta & IRouteMeta,
				},
			],
		},
		{
			path: '/:pathMatch(.*)',
			name: 'not-found',
			component: () => {
				return import('@/views/404.vue');
			},
			meta: {
				title: 'Page Not Found',
			} as RouteMeta & IRouteMeta,
		},
	],
});

router.beforeEach(async (to) => {
	document.title = (to.meta.title as string) ?? document.title;

	if (to.meta.requireAuth) {
		const userStore = useUserStore();

		const accounts = msalInstance.getAllAccounts();
		const account = msalInstance.getActiveAccount() ?? accounts[0];

		// No account at all → genuinely unauthenticated. Redirect to login (no loop:
		// the login route is not auth-guarded).
		if (!account) {
			return { name: ROUTE_NAMES.LOGIN, query: { redirect: to.fullPath } };
		}

		msalInstance.setActiveAccount(account);

		if (!userStore.loggedIn) {
			userStore.setUserData(account);
		}

		// Proactively refresh the token so the page's API calls start with a valid
		// one, but NEVER awaited: a slow or hung silent refresh must not wedge
		// navigation (every link goes dead until a page reload). The API layer
		// refreshes on demand and retries once on 401, and a genuinely dead session
		// raises the centralized re-auth prompt, so nothing here needs the result.
		void userStore.ensureAccessToken(false);

		// UX gate only (the API enforces roles). Skipped while we hold no token for
		// the LADDER this minRole belongs to, because then the roles are unknown
		// rather than insufficient: for the depot ladder that is a dead session,
		// which re-auth already prompts for, and for the 125 ladder it is a platform
		// token still in flight. Bouncing the user to Forbidden either way would be
		// both wrong and confusing. A token that genuinely carries no qualifying role
		// still lands on Forbidden as before.
		const minRole = to.meta.minRole as string | undefined;
		if (minRole && userStore.rolesKnownFor(minRole) && !userStore.canAtLeast(minRole)) {
			return { name: ROUTE_NAMES.FORBIDDEN };
		}

		return true;
	}
});

export default router;
